import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { socialLinks } from "@/mocks/home";

const SUBMIT_ADDR = "https://readdy.ai/api/form/davkq76oc082o2i1k15g";

type Status = "idle" | "sending" | "success" | "error";

export default function Contact() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();
    if (!name || !email || !message) {
      setStatus("error");
      setFormError(t("form.required"));
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") body.append(key, value);
    });

    setStatus("sending");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg ? serverMsg : t("form.error"),
        );
      }
    } catch {
      setStatus("error");
      setFormError(t("form.error"));
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300";
  const labelClass =
    "block font-label text-xs uppercase tracking-[0.16em] text-foreground-600 mb-2";

  return (
    <section id="contact" className="w-full bg-background-50 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary-400"></span>
              <span className="font-label text-sm uppercase tracking-[0.3em] text-primary-600">
                {t("contact.kicker")}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950">
              {t("contact.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              {t("contact.subtitle")}
            </p>

            <div className="mt-9 space-y-6">
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-100 text-primary-700 shrink-0">
                  <i className="ri-map-pin-line text-xl"></i>
                </span>
                <div>
                  <p className="font-label text-xs uppercase tracking-[0.16em] text-foreground-400">
                    {t("contact.locationLabel")}
                  </p>
                  <p className="font-heading text-lg font-semibold text-foreground-900">
                    {t("contact.locationValue")}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <div>
                  <p className="font-label text-xs uppercase tracking-[0.16em] text-foreground-400">
                    {t("contact.emailLabel")}
                  </p>
                  <a
                    href="mailto:hello@barewitnessinc.com"
                    className="font-heading text-lg font-semibold text-foreground-900 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {t("contact.emailValue")}
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-9 flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.id}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={social.label}
                  className="flex items-center justify-center w-11 h-11 rounded-full border border-background-200 text-foreground-700 hover:bg-primary-500 hover:border-primary-500 hover:text-accent-950 transition-colors cursor-pointer"
                >
                  <i className={`${social.icon} text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/50 p-6 md:p-8">
            <form
              data-readdy-form
              id="barewitness-contact-form"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="bw-name" className={labelClass}>
                    {t("form.name")}
                  </label>
                  <input
                    id="bw-name"
                    name="name"
                    type="text"
                    required
                    placeholder={t("form.namePlaceholder")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="bw-email" className={labelClass}>
                    {t("form.email")}
                  </label>
                  <input
                    id="bw-email"
                    name="email"
                    type="email"
                    required
                    placeholder={t("form.emailPlaceholder")}
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="bw-subject" className={labelClass}>
                  {t("form.subject")}
                </label>
                <select id="bw-subject" name="subject" className={inputClass} defaultValue="general">
                  <option value="general">{t("form.subjectGeneral")}</option>
                  <option value="press">{t("form.subjectPress")}</option>
                  <option value="collaboration">{t("form.subjectCollab")}</option>
                  <option value="donation">{t("form.subjectDonate")}</option>
                </select>
              </div>

              <div>
                <label htmlFor="bw-message" className={labelClass}>
                  {t("form.message")}
                </label>
                <textarea
                  id="bw-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  placeholder={t("form.messagePlaceholder")}
                  className={`${inputClass} resize-none`}
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field-honeypot"
              />

              {status === "success" && (
                <p className="rounded-md border border-secondary-200 bg-secondary-100 text-secondary-900 text-sm px-4 py-3">
                  {t("form.success")}
                </p>
              )}
              {status === "error" && (
                <p className="rounded-md border border-accent-200 bg-accent-100 text-accent-900 text-sm px-4 py-3">
                  {formError || t("form.error")}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-accent-950 font-label text-base uppercase tracking-[0.14em] px-6 py-3.5 rounded-md transition-colors cursor-pointer"
              >
                <i className={status === "sending" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                {status === "sending" ? t("form.sending") : t("form.submit")}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}